import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Auth.css';

const SignUp: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState('');

  const { signUp, error: authError, clearError } = useAuth();
  const navigate = useNavigate();

  // Clear errors when form fields change
  React.useEffect(() => {
    clearError();
    setLocalError('');
  }, [email, password, confirmPassword, firstName, lastName, clearError]);

  const validateForm = (): boolean => {
    if (!email.trim()) {
      setLocalError('Email is required');
      return false;
    }

    if (!password.trim()) {
      setLocalError('Password is required');
      return false;
    }

    if (password.length < 12) {
      setLocalError('Password must be at least 12 characters long');
      return false;
    }

    if (!/[A-Z]/.test(password)) {
      setLocalError('Password must include at least one uppercase letter');
      return false;
    }

    if (!/[a-z]/.test(password)) {
      setLocalError('Password must include at least one lowercase letter');
      return false;
    }

    if (!/[0-9]/.test(password)) {
      setLocalError('Password must include at least one number');
      return false;
    }

    if (!/[^A-Za-z0-9]/.test(password)) {
      setLocalError('Password must include at least one symbol (e.g., !@#$%)');
      return false;
    }

    if (password !== confirmPassword) {
      setLocalError('Passwords do not match');
      return false;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setLocalError('Please enter a valid email address');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setLocalError('');
    clearError();

    try {
      await signUp(email.trim(), password, firstName.trim() || undefined, lastName.trim() || undefined);
      navigate('/confirm-signup', { state: { email: email.trim() } });
    } catch (error: any) {
      // Error is handled by AuthContext
      if (!authError) {
        setLocalError(error.message || 'Failed to create account');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo">
            <span className="logo-icon">🎒</span>
            <h1>Trip Packing Planner</h1>
          </div>
          <h2>Create Account</h2>
          <p>Join us and start organizing your trips!</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {(authError || localError) && (
            <div className="error-message">
              {authError?.message || localError}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="firstName">First Name</label>
            <input
              type="text"
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={isLoading}
              placeholder="Enter your first name"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="lastName">Last Name</label>
            <input
              type="text"
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={isLoading}
              placeholder="Enter your last name"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
              placeholder="Enter your email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              placeholder="Enter your password"
              minLength={8}
            />
            <small className="form-help">
              At least 12 characters with uppercase, lowercase, number, and symbol
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={isLoading}
              placeholder="Confirm your password"
            />
          </div>

          <button
            type="submit"
            className="auth-button primary"
            disabled={isLoading}
          >
            {isLoading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Already have an account?{' '}
            <Link to="/login" className="auth-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
